import { useMemo } from "react";
import {
  SpaceSwitcher,
  type SwitcherProject,
} from "@/components/panels/space-switcher";
import { AgentListFrame } from "@/components/shapes/agent-list-frame";
import { HtmlFrame } from "@/components/shapes/html-frame";
import { MemoryFrame } from "@/components/shapes/memory-frame";
import { TaskboardFrame } from "@/components/shapes/taskboard-frame";
import { DenkerLogo } from "@/components/ui/logo";
import { AgentCursorCompact } from "@/desktop/agent-cursor-compact";
import { CompactWidgets } from "@/desktop/compact-widgets";
import {
  PROJECT_ID,
  SPACE_ID,
  demoFrame,
  frameProps,
} from "./dolcetto-media-data";
import { makeTimeHtml } from "./dolcetto-media-make-time-html";
import { MediaItem } from "./dolcetto-media-item";
import { MediaDenkerIntro } from "./dolcetto-media-denker-intro";
import {
  useMediaDesktopOverlays,
  useMediaScene,
  useSeedMediaData,
} from "./dolcetto-media-hooks";
import "./dolcetto-media-page.css";

export function DolcettoMediaPage() {
  const seeded = useSeedMediaData();
  const scene = useMediaScene();
  useMediaDesktopOverlays(scene, seeded);

  const frames = useMemo(
    () => ({
      agent: demoFrame(
        "media-frame-agent-list",
        "agent_list",
        "Your Team",
        {},
        780,
        560,
      ),
      taskboard: demoFrame(
        "media-frame-taskboard",
        "taskboard",
        "Taskboard",
        {},
        1160,
        640,
      ),
      html: demoFrame(
        "media-frame-html",
        "html",
        "Make Time takeaway",
        { html: makeTimeHtml() },
        1060,
        720,
        { html_frame_theme_mode: "light" },
      ),
      memory: demoFrame(
        "media-frame-memory",
        "memory",
        "Books Denker remembers",
        {},
        980,
        660,
      ),
    }),
    [],
  );

  const switcherProjects: SwitcherProject[] = useMemo(
    () => [
      {
        id: PROJECT_ID,
        name: "Dolcetto",
        color: "#30D158",
        active: true,
        isHome: true,
        isPinned: true,
        spaces: [
          { id: SPACE_ID, label: "Here", active: true, color: "#30D158" },
          { id: "media-space-ui", label: "UI", color: "#64D2FF" },
          { id: "media-space-agents", label: "Agents", color: "#BF5AF2" },
        ],
      },
      {
        id: "media-project-growth",
        name: "Growth",
        color: "#64D2FF",
        spaces: [{ id: "media-growth-launch", label: "Launch" }],
      },
      {
        id: "media-project-lab",
        name: "Lab",
        color: "#FFB020",
        spaces: [{ id: "media-lab-tests", label: "Tests" }],
      },
    ],
    [],
  );

  if (!seeded) return null;

  return (
    <main
      className="dolcetto-media-page"
      data-scene={scene}
      data-testid="dolcetto-media-page"
    >
      <div className="dolcetto-media-stage" data-scene={scene}>
        <MediaItem
          id="logo"
          scene={scene}
          activeWidth="260px"
          activeHeight="260px"
          flatLeft="4.5vw"
          flatTop="5vh"
          flatWidth="74px"
          flatHeight="74px"
          z={16}
        >
          <div className="dolcetto-media-tool-shell dolcetto-media-logo">
            <div className="dolcetto-media-logo-mark">
              <DenkerLogo
                variant="symbol"
                color="currentColor"
                height={72}
                className="text-accent"
              />
            </div>
          </div>
        </MediaItem>

        <MediaItem
          id="switcher"
          scene={scene}
          activeWidth="640px"
          activeHeight="104px"
          flatLeft="10vw"
          flatTop="5.9vh"
          flatWidth="420px"
          flatHeight="58px"
          z={15}
        >
          <div className="dolcetto-media-tool-shell dolcetto-media-switcher-wrap">
            <SpaceSwitcher
              projects={switcherProjects}
              density="compact"
              collapseToCenterProject
              autoExpandCompact
              agentSpeaking={false}
              className="dolcetto-media-clean-switcher"
              onSelectProject={() => {}}
              onSelectSpace={() => {}}
              data-testid="media-space-switcher"
            />
          </div>
        </MediaItem>

        <MediaItem
          id="agent"
          scene={scene}
          activeWidth="min(84vw, 980px)"
          activeHeight="min(76vh, 680px)"
          flatLeft="4vw"
          flatTop="14vh"
          flatWidth="34vw"
          flatHeight="39vh"
          z={7}
        >
          <div className="dolcetto-media-frame-shell">
            <AgentListFrame {...frameProps(frames.agent)} />
          </div>
        </MediaItem>

        <MediaItem
          id="taskboard"
          scene={scene}
          activeWidth="min(88vw, 1180px)"
          activeHeight="min(72vh, 680px)"
          flatLeft="42vw"
          flatTop="11vh"
          flatWidth="53vw"
          flatHeight="42vh"
          z={8}
        >
          <div className="dolcetto-media-frame-shell">
            <TaskboardFrame {...frameProps(frames.taskboard)} />
          </div>
        </MediaItem>

        <MediaItem
          id="html"
          scene={scene}
          activeWidth="min(88vw, 1120px)"
          activeHeight="min(80vh, 760px)"
          flatLeft="5vw"
          flatTop="57vh"
          flatWidth="45vw"
          flatHeight="39vh"
          z={6}
        >
          <div className="dolcetto-media-frame-shell">
            <HtmlFrame {...frameProps(frames.html)} />
          </div>
        </MediaItem>

        <MediaItem
          id="memory"
          scene={scene}
          activeWidth="min(86vw, 1080px)"
          activeHeight="min(76vh, 700px)"
          flatLeft="53vw"
          flatTop="57vh"
          flatWidth="42vw"
          flatHeight="39vh"
          z={6}
        >
          <div className="dolcetto-media-frame-shell">
            <MemoryFrame {...frameProps(frames.memory)} />
          </div>
        </MediaItem>
      </div>
      <MediaDenkerIntro visible={scene === "logo"} />
      <div className="dolcetto-media-product-chrome">
        <CompactWidgets />
        <AgentCursorCompact visible={scene === "bubble" || scene === "flat"} />
      </div>
    </main>
  );
}
